import React, { FC, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { isToday, format, isThisYear } from 'date-fns'
import classNames from 'classnames'

import { MessageProps } from './Message.types'
import { URL_MEDIA } from 'out-game/constants'
import DefaultAvatar from 'out-game/images/DefaultAvatar.svg'
import './Message.scss'
import { useTranslationStrict, useValuesByLanguage } from 'out-game/hooks'

const formatDate = (date: Date): string => {
  const formatStr =
    (isToday(date) && 'HH:mm') || (isThisYear(date) && 'dd/MM') || 'dd/MM/yy'

  return format(date, formatStr)
}

const Message: FC<MessageProps> = ({ message, user }) => {
  const { author, text, is_system, created_at, avatar, is_staff, is_deleted, reason_for_deletion } = message
  
  const { t } = useTranslationStrict('chat')
  
  const getValueByLang = useValuesByLanguage()

  const [formattedDate, setFormattedDate] = useState<string>('')
  const isAuthorAnotherUser = author !== user && !is_system

  const _date = useMemo(() => new Date(created_at), [created_at])
  const messageClass = classNames('message js-general-chat', {
    message_local: author === user,
    message_remote: isAuthorAnotherUser,
    message_deleted: is_deleted,
    'message__author-staff': is_staff
  })

  useEffect(() => {
    setFormattedDate(formatDate(_date))
  }, [_date])

  return (
    <div className={messageClass}>
      {isAuthorAnotherUser && (
        <Link
          className="message__author message__author_remote"
          to={`/profile/${author}/items/`}
        >
          <img
            className={classNames('author__avatar', {
              'author__avatar--default': !avatar,
            })}
            src={avatar ? URL_MEDIA + avatar : DefaultAvatar}
            alt="user avatar"
          />
        </Link>
      )}
      <div className="text-container">
        {isAuthorAnotherUser && (
          <div className="message__author-nickname">{author}</div>
        )}
        {is_deleted ? (
          <div>
            {t('messageDeleted')}.{' '}
            {reason_for_deletion && `${t('reason')}: ${reason_for_deletion}`}
          </div>
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: is_system ? getValueByLang(message, 'text') : text,
            }}
          />
        )}
      </div>
      <p className="message__date">{formattedDate}</p>
    </div>
  )
}

export default Message
